import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { API } from '../App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Users, CheckCircle, Building2, GraduationCap, 
  TrendingUp, TrendingDown, Minus, School, UserCheck, Clock,
  ArrowRight, RefreshCw
} from "lucide-react";

const ACTIVE_WINDOWS = [
  { label: "15 min", minutes: 15 },
  { label: "1 hour", minutes: 60 },
  { label: "Today", minutes: 1440 },
];

const ActiveUsersCard = () => {
  const [minutes, setMinutes] = useState(15);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (m) => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/console/active-users?minutes=${m}`, { credentials: "include" });
      if (res.ok) setData(await res.json());
    } catch (e) { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => { load(minutes); }, [minutes, load]);
  useEffect(() => {
    const id = setInterval(() => load(minutes), 30000);
    return () => clearInterval(id);
  }, [minutes, load]);

  const t = data?.by_type || {};
  const breakdown = [
    { key: "students", label: "Students", value: t.students || 0, icon: GraduationCap, grad: "from-blue-500 to-blue-600" },
    { key: "parents", label: "Parents", value: t.parents || 0, icon: Users, grad: "from-purple-500 to-purple-600" },
    { key: "teachers", label: "Teachers", value: t.teachers || 0, icon: School, grad: "from-emerald-500 to-emerald-600" },
  ];
  const windowLabel = ACTIVE_WINDOWS.find((w) => w.minutes === minutes)?.label.toLowerCase();

  return (
    <Card className="border-slate-200" data-testid="active-users-card">
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="flex items-center gap-2">
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"></span>
            </span>
            Active Users
          </CardTitle>
          <div className="flex gap-1 bg-slate-100 rounded-lg p-0.5" data-testid="active-users-window">
            {ACTIVE_WINDOWS.map((w) => (
              <button
                key={w.minutes}
                onClick={() => setMinutes(w.minutes)}
                data-testid={`active-window-${w.minutes}`}
                className={`text-xs px-2.5 py-1 rounded-md transition-colors ${minutes === w.minutes ? "bg-white shadow text-slate-900 font-medium" : "text-slate-500 hover:text-slate-700"}`}
              >
                {w.label}
              </button>
            ))}
          </div>
        </div>
        <CardDescription>
          Logged-in / active in the last {windowLabel} · auto-refreshes every 30s
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-end gap-3 mb-4">
          <p className="text-4xl font-bold text-slate-900" data-testid="active-users-total">
            {loading && !data ? "—" : (data?.total ?? 0)}
          </p>
          <p className="text-sm text-slate-500 mb-1.5">total active now</p>
        </div>
        <div className="grid grid-cols-3 gap-3">
          {breakdown.map((b) => {
            const Icon = b.icon;
            return (
              <div key={b.key} className={`bg-gradient-to-br ${b.grad} text-white rounded-xl p-3 shadow-sm`} data-testid={`active-users-${b.key}`}>
                <Icon className="h-5 w-5 text-white/90 mb-2" />
                <p className="text-2xl font-bold text-white" data-testid={`active-users-${b.key}-count`}>{b.value}</p>
                <p className="text-xs text-white/80">{b.label}</p>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};


const Dashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [schoolStats, setSchoolStats] = useState(null);
  const [recentSchools, setRecentSchools] = useState([]);
  const [trends, setTrends] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const fetchStats = useCallback(async () => {
    try {
      const [dashboardRes, schoolsRes, trendsRes] = await Promise.all([
        fetch(`${API}/dashboard/stats`, { credentials: "include" }),
        fetch(`${API}/admin/schools/stats`, { credentials: "include" }).catch(() => null),
        fetch(`${API}/admin/console/dashboard-trends`, { credentials: "include" }).catch(() => null)
      ]);
      
      if (dashboardRes.ok) {
        const data = await dashboardRes.json();
        setStats(data);
      }
      
      if (schoolsRes?.ok) {
        const schoolData = await schoolsRes.json();
        setSchoolStats(schoolData);
        setRecentSchools(schoolData.recent_schools || []);
      }

      if (trendsRes?.ok) {
        const td = await trendsRes.json();
        setTrends(td.trends || null);
      }
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    } finally {
      setLoading(false);
    }
  }, []);
  
  useEffect(() => {
    fetchStats();
  }, [fetchStats]);
  
  const statCards = [
    {
      label: 'Total Subscribers',
      value: stats?.total_subscribers || 0,
      icon: Users,
      color: 'from-blue-500 to-blue-600',
      bgColor: 'bg-blue-50',
      textColor: 'text-blue-600',
      link: '/admin/subscribers',
      trendKey: 'total_subscribers',
    },
    {
      label: 'Active Learners',
      value: stats?.active_subscribers || 0,
      icon: UserCheck,
      color: 'from-emerald-500 to-emerald-600',
      bgColor: 'bg-emerald-50',
      textColor: 'text-emerald-600',
      link: '/admin/subscribers',
      trendKey: 'active_learners',
    },
    {
      label: 'Registered Schools',
      value: schoolStats?.total_schools || 0,
      icon: Building2,
      color: 'from-purple-500 to-purple-600',
      bgColor: 'bg-purple-50',
      textColor: 'text-purple-600',
      link: '/admin/schools',
      trendKey: 'registered_schools',
    },
    {
      label: 'School Students',
      value: schoolStats?.total_school_students || 0,
      icon: GraduationCap,
      color: 'from-amber-500 to-amber-600',
      bgColor: 'bg-amber-50',
      textColor: 'text-amber-600',
      link: '/admin/schools',
      trendKey: 'school_students',
    },
  ];
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }
  
  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">
            Dashboard
          </h1>
          <p className="text-slate-600 mt-1">
            Overview of Eduzy platform
          </p>
        </div>
        <Button 
          variant="outline" 
          size="sm"
          onClick={fetchStats}
          className="gap-2"
        >
          <RefreshCw className="h-4 w-4" />
          Refresh
        </Button>
      </div>
      
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat) => {
          const Icon = stat.icon;
          const trend = trends?.[stat.trendKey];
          const TrendIcon = trend ? (trend.direction === 'up' ? TrendingUp : trend.direction === 'down' ? TrendingDown : Minus) : null;
          return (
            <Card 
              key={stat.label}
              className={`cursor-pointer hover:shadow-xl hover:-translate-y-0.5 transition-all border-0 bg-gradient-to-br ${stat.color} text-white shadow-md`}
              onClick={() => navigate(stat.link)}
              data-testid={`stat-${stat.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-medium text-white/80 uppercase tracking-wide">
                      {stat.label}
                    </p>
                    <p className="text-3xl font-bold text-white mt-2">
                      {stat.value.toLocaleString()}
                    </p>
                    {trend && (
                      <span
                        className="inline-flex items-center gap-1 mt-2 text-xs font-semibold bg-white/20 rounded-full px-2 py-0.5"
                        data-testid={`trend-${stat.trendKey}`}
                        title="Change vs last week"
                      >
                        <TrendIcon className="h-3 w-3" />
                        {trend.change_pct > 0 ? '+' : ''}{trend.change_pct}%
                        <span className="text-white/70 font-normal">vs last week</span>
                      </span>
                    )}
                    <p className="text-xs text-white/90 mt-2 flex items-center gap-1">
                      View details <ArrowRight className="h-3 w-3" />
                    </p>
                  </div>
                  <div className="bg-white/20 p-3 rounded-xl">
                    <Icon className="h-6 w-6 text-white" />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
      
      {/* Active Users */}
      <ActiveUsersCard />
      
      {/* Subscriber Overview */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-emerald-600" />
            Platform Overview
          </CardTitle>
          <CardDescription>
            Key metrics and performance indicators
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-6" data-testid="subscriber-overview-panel">
            <div className="text-center p-4 bg-blue-50 rounded-xl" data-testid="stat-parent-accounts">
              <p className="text-3xl font-bold text-blue-600">{stats?.total_parents || 0}</p>
              <p className="text-sm text-slate-600 mt-1">Parent Accounts</p>
            </div>
            <div className="text-center p-4 bg-emerald-50 rounded-xl" data-testid="stat-active-trials">
              <p className="text-3xl font-bold text-emerald-600">{stats?.active_trials || 0}</p>
              <p className="text-sm text-slate-600 mt-1">Active Free Trials</p>
            </div>
            <div className="text-center p-4 bg-purple-50 rounded-xl" data-testid="stat-paid-subscriptions">
              <p className="text-3xl font-bold text-purple-600">{stats?.paid_subscriptions || 0}</p>
              <p className="text-sm text-slate-600 mt-1">Paid Subscribers</p>
            </div>
            <div className="text-center p-4 bg-teal-50 rounded-xl" data-testid="stat-new-today">
              <p className="text-3xl font-bold text-teal-600">{stats?.new_today || 0}</p>
              <p className="text-sm text-slate-600 mt-1">New Today</p>
            </div>
            <div className="text-center p-4 bg-indigo-50 rounded-xl" data-testid="stat-new-week">
              <p className="text-3xl font-bold text-indigo-600">{stats?.new_this_week || 0}</p>
              <p className="text-sm text-slate-600 mt-1">New This Week</p>
            </div>
            <div className="text-center p-4 bg-amber-50 rounded-xl" data-testid="stat-avg-score">
              <p className="text-3xl font-bold text-amber-600">{stats?.avg_score || 0}%</p>
              <p className="text-sm text-slate-600 mt-1">Avg. Score</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;
